/**
 * tot-verify.js
 * ─────────────
 * Two-tier certificate verification for TOT holographic sticker QR scans.
 *
 * URL routing:
 *   ?t=<scan_token>   → QR scan path — full provenance + ownership chain
 *   ?s=<serial>       → Manual entry path — minimal "serial registered" card
 *   ?approve=<token>  → Transfer approval path — approve pending ownership transfer
 *
 * Security:
 *   - scan_token cannot be reconstructed from the serial number
 *   - Full provenance only accessible via QR scan (physical possession proof)
 *   - All writes go through Supabase Edge Functions (service role key never client-side)
 *   - All DOM writes use textContent or createElement — no innerHTML
 */

(function () {
  'use strict';

  // ─── State elements ────────────────────────────────────────────────────────

  var ROOT                 = document.getElementById('tot-verify-root');
  var LOADING              = document.getElementById('tot-verify-loading');
  var NO_SERIAL            = document.getElementById('tot-verify-no-serial');
  var FOUND                = document.getElementById('tot-verify-found');
  var NOT_FOUND            = document.getElementById('tot-verify-not-found');
  var ERROR_STATE          = document.getElementById('tot-verify-error');
  var MINIMAL              = document.getElementById('tot-verify-minimal');
  var TRANSFER_APPROVED    = document.getElementById('tot-verify-transfer-approved');

  if (!ROOT) return;

  var SUPABASE_URL      = ROOT.dataset.supabaseUrl     || '';
  var SUPABASE_ANON_KEY = ROOT.dataset.supabaseAnonKey || '';
  var EDGE_BASE         = SUPABASE_URL + '/functions/v1';

  var ALL_STATES = [
    LOADING, NO_SERIAL, FOUND, NOT_FOUND, ERROR_STATE,
    MINIMAL, TRANSFER_APPROVED
  ];

  // ─── Utilities ─────────────────────────────────────────────────────────────

  function showState(el) {
    ALL_STATES.forEach(function (s) {
      if (!s) return;
      s.classList.toggle('tot-verify-state--hidden', s !== el);
    });
  }

  function clearChildren(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  function formatDate(dateStr) {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch (_) { return dateStr; }
  }

  function formatMonthYear(dateStr) {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        month: 'long', year: 'numeric'
      });
    } catch (_) { return null; }
  }

  function addProvenanceRow(dl, label, value) {
    if (!value) return;
    var div = document.createElement('div');
    div.className = 'tot-verify-provenance__row';
    var dt = document.createElement('dt');
    dt.textContent = label;
    var dd = document.createElement('dd');
    dd.textContent = value;
    div.appendChild(dt);
    div.appendChild(dd);
    dl.appendChild(div);
  }

  function makeEl(tag, cls, text) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  // ─── Supabase REST helpers ─────────────────────────────────────────────────

  function supabaseHeaders() {
    return {
      'apikey':        SUPABASE_ANON_KEY,
      'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
      'Content-Type':  'application/json',
    };
  }

  function rpc(name, params) {
    return fetch(SUPABASE_URL + '/rest/v1/rpc/' + name, {
      method:  'POST',
      headers: supabaseHeaders(),
      body:    JSON.stringify(params),
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function fetchOwnershipChain(serialNumber) {
    var url = SUPABASE_URL + '/rest/v1/coa_ownership'
      + '?serial_number=eq.' + encodeURIComponent(serialNumber)
      + '&select=registered_at,transferred_at,is_current'
      + '&order=registered_at.asc';
    return fetch(url, { headers: supabaseHeaders() })
      .then(function (res) { return res.ok ? res.json() : []; })
      .catch(function () { return []; });
  }

  function edgePost(functionName, body) {
    return fetch(EDGE_BASE + '/' + functionName, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
      body:    JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.error || 'Request failed (' + res.status + ')');
        return data;
      });
    });
  }

  // ─── Ownership section ─────────────────────────────────────────────────────

  function renderOwnershipChain(container, records, scanToken) {
    clearChildren(container);

    var section = makeEl('div', 'tot-ownership-section');

    // Chain of custody timeline
    if (records.length > 0) {
      var heading = makeEl('p', 'tot-ownership-heading', 'Chain of Custody');
      section.appendChild(heading);

      var timeline = makeEl('ol', 'tot-ownership-chain');
      records.forEach(function (rec, i) {
        var li = makeEl('li', 'tot-ownership-chain__entry');

        var badge = makeEl('span', 'tot-ownership-chain__badge');
        badge.textContent = (i === 0) ? 'First Owner' : 'Transfer ' + i;
        li.appendChild(badge);

        var dates = makeEl('span', 'tot-ownership-chain__dates');
        var from = formatMonthYear(rec.registered_at);
        var to   = rec.transferred_at ? formatMonthYear(rec.transferred_at) : 'Present';
        dates.textContent = from + (rec.transferred_at ? ' → ' + to : ' → Present');
        li.appendChild(dates);

        timeline.appendChild(li);
      });
      section.appendChild(timeline);
    }

    // Find if there's a current owner
    var hasCurrent = records.some(function (r) { return r.is_current; });

    if (!hasCurrent) {
      // No owner registered — show registration form
      renderRegisterForm(section, scanToken);
    } else {
      // Has owner — show transfer request form
      renderTransferForm(section, scanToken);
    }

    container.appendChild(section);
  }

  function renderRegisterForm(container, scanToken) {
    var wrap = makeEl('div', 'tot-ownership-form-wrap');

    var heading = makeEl('p', 'tot-ownership-form__title', 'Activate Your Lifetime Guarantee');
    var sub = makeEl('p', 'tot-ownership-form__sub',
      'Register ownership to activate your lifetime authenticity guarantee and receive your digital certificate.');
    wrap.appendChild(heading);
    wrap.appendChild(sub);

    var form = makeEl('form', 'tot-ownership-form');
    form.id = 'tot-register-form';

    var nameInput = makeEl('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Your full name';
    nameInput.className = 'tot-ownership-form__input';
    nameInput.required = true;
    nameInput.id = 'tot-reg-name';

    var emailInput = makeEl('input');
    emailInput.type = 'email';
    emailInput.placeholder = 'Your email address';
    emailInput.className = 'tot-ownership-form__input';
    emailInput.required = true;
    emailInput.id = 'tot-reg-email';

    var btn = makeEl('button', 'tot-ownership-form__btn', 'Register Ownership');
    btn.type = 'submit';

    var msg = makeEl('p', 'tot-ownership-form__msg');
    msg.id = 'tot-reg-msg';

    form.appendChild(nameInput);
    form.appendChild(emailInput);
    form.appendChild(btn);
    form.appendChild(msg);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var name  = nameInput.value.trim();
      var email = emailInput.value.trim();
      if (!name || !email) return;

      btn.disabled = true;
      btn.textContent = 'Registering…';
      msg.textContent = '';

      edgePost('register-ownership', { scan_token: scanToken, owner_name: name, owner_email: email })
        .then(function () {
          form.style.display = 'none';
          msg.className = 'tot-ownership-form__msg tot-ownership-form__msg--success';
          msg.textContent = 'Ownership registered. Check your email for confirmation.';
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = 'Register Ownership';
          msg.className = 'tot-ownership-form__msg tot-ownership-form__msg--error';
          msg.textContent = err.message || 'Registration failed. Please try again.';
        });
    });

    wrap.appendChild(form);
    container.appendChild(wrap);
  }

  function renderTransferForm(container, scanToken) {
    var details = makeEl('details', 'tot-transfer-details');
    var summary = makeEl('summary', 'tot-transfer-summary', 'Transfer Ownership');
    details.appendChild(summary);

    var sub = makeEl('p', 'tot-transfer-sub',
      'If you have purchased or received this item, enter your details to request an ownership transfer. The current registered owner will be asked to approve.');
    details.appendChild(sub);

    var form = makeEl('form', 'tot-ownership-form tot-ownership-form--transfer');

    var nameInput = makeEl('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Your full name';
    nameInput.className = 'tot-ownership-form__input';
    nameInput.required = true;

    var emailInput = makeEl('input');
    emailInput.type = 'email';
    emailInput.placeholder = 'Your email address';
    emailInput.className = 'tot-ownership-form__input';
    emailInput.required = true;

    var btn = makeEl('button', 'tot-ownership-form__btn', 'Request Transfer');
    btn.type = 'submit';

    var msg = makeEl('p', 'tot-ownership-form__msg');

    form.appendChild(nameInput);
    form.appendChild(emailInput);
    form.appendChild(btn);
    form.appendChild(msg);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var name  = nameInput.value.trim();
      var email = emailInput.value.trim();
      if (!name || !email) return;

      btn.disabled = true;
      btn.textContent = 'Sending request…';
      msg.textContent = '';

      edgePost('request-transfer', { scan_token: scanToken, requester_name: name, requester_email: email })
        .then(function () {
          form.style.display = 'none';
          msg.className = 'tot-ownership-form__msg tot-ownership-form__msg--success';
          msg.textContent = 'Transfer request sent. The current owner will receive an email to approve.';
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = 'Request Transfer';
          msg.className = 'tot-ownership-form__msg tot-ownership-form__msg--error';
          msg.textContent = err.message || 'Request failed. Please try again.';
        });
    });

    details.appendChild(form);
    container.appendChild(details);
  }

  // ─── QR scan path — full provenance ────────────────────────────────────────

  function renderFound(item, scanToken) {
    var dl = document.getElementById('tot-verify-provenance-list');
    if (dl) {
      clearChildren(dl);
      if (item.item_name && item.item_name !== 'UNASSIGNED') {
        addProvenanceRow(dl, 'Item', item.item_name);
      }
      addProvenanceRow(dl, 'Athlete',         item.athlete);
      addProvenanceRow(dl, 'Sport',           item.sport);
      addProvenanceRow(dl, 'Item Type',       item.item_type);
      addProvenanceRow(dl, 'Condition',       item.condition);
      addProvenanceRow(dl, 'Auth. Source',    item.auth_source);
      addProvenanceRow(dl, 'Certificate No.', item.auth_cert_id);
      addProvenanceRow(dl, 'Auth. Date',      formatDate(item.auth_date));
      addProvenanceRow(dl, 'Registered',      formatDate(item.created_at));
    }

    var serialEl = document.getElementById('tot-verify-serial-display');
    if (serialEl) serialEl.textContent = item.serial_number;

    // ── Item image ─────────────────────────────────────────────────────────
    var imgWrap = document.getElementById('tot-verify-item-image');
    if (imgWrap) {
      clearChildren(imgWrap);
      if (item.image_url) {
        var img = document.createElement('img');
        img.src = item.image_url;
        img.alt = item.item_name || 'Authenticated item';
        img.className = 'tot-verify-item-img';
        imgWrap.appendChild(img);
        imgWrap.style.display = 'block';
      } else {
        imgWrap.style.display = 'none';
      }
    }

    var scanNote = document.getElementById('tot-verify-scan-note');
    if (scanNote) {
      var count = (item.scan_count || 0) + 1;
      scanNote.textContent = 'This certificate has been scanned '
        + count + (count === 1 ? ' time' : ' times');
    }

    showState(FOUND);

    // Load ownership chain asynchronously
    var ownershipContainer = document.getElementById('tot-verify-ownership-section');
    if (ownershipContainer && item.item_name !== 'UNASSIGNED') {
      fetchOwnershipChain(item.serial_number).then(function (records) {
        renderOwnershipChain(ownershipContainer, records, scanToken);
      });
    }
  }

  function verifyByToken(token) {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) { showState(ERROR_STATE); return; }
    showState(LOADING);

    rpc('log_sticker_scan_by_token', { p_token: token })
      .then(function (rows) {
        if (!rows || rows.length === 0) { showState(NOT_FOUND); return; }
        renderFound(rows[0], token);
      })
      .catch(function (err) {
        console.warn('[TOT verify] scan_token lookup error:', err);
        showState(ERROR_STATE);
      });
  }

  // ─── Serial-only path — minimal view ───────────────────────────────────────

  function verifyBySerial(serial) {
    serial = (serial || '').trim().toUpperCase();

    // Accept both old sequential format and new random format
    if (!/^TOT-[A-Z0-9]{6}$/.test(serial)) {
      var msgEl = document.getElementById('tot-verify-not-found-msg');
      if (msgEl) {
        msgEl.textContent = '"' + serial + '" is not a valid TOT serial. Serials follow the pattern TOT-XXXXXX.';
      }
      showState(NOT_FOUND);
      return;
    }

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) { showState(ERROR_STATE); return; }
    showState(LOADING);

    rpc('get_item_by_serial', { p_serial: serial })
      .then(function (rows) {
        if (!rows || rows.length === 0) { showState(NOT_FOUND); return; }

        var item = rows[0];
        var serialDisplay = document.getElementById('tot-verify-minimal-serial');
        var itemDisplay   = document.getElementById('tot-verify-minimal-item');

        if (serialDisplay) serialDisplay.textContent = item.serial_number;
        if (itemDisplay && item.item_name && item.item_name !== 'UNASSIGNED') {
          itemDisplay.textContent = item.item_name;
        }
        showState(MINIMAL);
      })
      .catch(function (err) {
        console.warn('[TOT verify] serial lookup error:', err);
        showState(ERROR_STATE);
      });
  }

  // ─── Transfer approval path ─────────────────────────────────────────────────

  function handleApprove(transferToken) {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) { showState(ERROR_STATE); return; }
    showState(LOADING);

    edgePost('approve-transfer', { transfer_token: transferToken })
      .then(function (data) {
        var nameEl = document.getElementById('tot-verify-approved-owner');
        var itemEl = document.getElementById('tot-verify-approved-item');
        if (nameEl && data.new_owner)     nameEl.textContent = data.new_owner;
        if (itemEl && data.serial_number) itemEl.textContent = data.serial_number;
        showState(TRANSFER_APPROVED);
      })
      .catch(function (err) {
        console.warn('[TOT verify] approve-transfer error:', err);
        // Show error state with custom message if possible
        var errMsg = document.getElementById('tot-verify-error-msg');
        if (errMsg) errMsg.textContent = err.message || 'Transfer could not be completed.';
        showState(ERROR_STATE);
      });
  }

  // ─── Entry point ──────────────────────────────────────────────────────────

  function init() {
    var params       = new URLSearchParams(window.location.search);
    var scanToken    = params.get('t');
    var serial       = params.get('s');
    var approveToken = params.get('approve');

    // Strip sensitive tokens from URL immediately — prevents address-bar copying,
    // keeps tokens out of browser history, and stops screenshots from leaking the
    // scan_token. The token is already captured above; this is safe to do now.
    if (scanToken || approveToken) {
      history.replaceState(null, '', window.location.pathname);
    }

    if (approveToken) {
      handleApprove(approveToken);
    } else if (scanToken) {
      verifyByToken(scanToken);
    } else if (serial) {
      verifyBySerial(serial);
    } else {
      showState(NO_SERIAL);
    }

    // Manual serial entry (shown on no-serial state)
    var input  = document.getElementById('tot-serial-input');
    var button = document.getElementById('tot-serial-submit');
    if (button && input) {
      button.addEventListener('click', function () {
        var val = (input.value || '').trim();
        if (val) verifyBySerial(val);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          var val = (input.value || '').trim();
          if (val) verifyBySerial(val);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
